<template>
  <AdminLayout>
    <Head title="Aging Inventory Report" />

    <div class="headline flex flex-col md:flex-row justify-between items-start md:items-center mx-4 sm:mx-12">
      <h2 class="text-2xl font-semibold text-blue-600">Aging Inventory Report</h2>

      <div class="relative inline-flex self-start my-5">
        <div class="input-group mr-2">
          <select
            v-model="form.location_id"
            id="product-regulated"
            class="bg-gray-100"
          >
            <option
              v-for="location in sortedLocations"
              :key="location.id"
              :value="location.id"
            >
              {{ location.name }}
            </option>
          </select>
        </div>

        <div class="input-group mr-2">
          <select
            v-model="form.supplier_id"
            class="bg-gray-100"
          >
            <option :value="null">All Brands</option>
            <option
              v-for="supplier in suppliers.data"
              :key="supplier.id"
              :value="supplier.id"
            >
              {{ supplier.name }}
            </option>
          </select>
        </div>

        <button
          @click="download()"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
        >
          Download
        </button>
      </div>
    </div>

    <!-- TABLE -->
    <div class="mb-4">
      <div class="">
        <div class="">
          <div class="table-shadow relative overflow-hidden">
            <div class="table-container overflow-x-auto relative">
              <table class="w-full whitespace-nowrap overflow-hidden">
                <thead>
                  <tr class="header-row">
                    <th></th>
                    <th></th>
                    <th></th>
                    <th
                      class="highlight text-center"
                      colspan="4"
                    >
                      0 - 30 Days
                    </th>
                    <th
                      class="text-center"
                      colspan="4"
                    >
                      31 - 60 Days
                    </th>
                    <th
                      class="highlight text-center"
                      colspan="4"
                    >
                      61- 90 Days
                    </th>
                    <th
                      class="text-center"
                      colspan="4"
                    >
                      91 - 120 Days
                    </th>
                    <th
                      class="highlight text-center"
                      colspan="4"
                    >
                      &gt; 120 Days
                    </th>
                  </tr>

                  <tr class="header-row">
                    <th></th>
                    <th></th>
                    <th></th>
                    <!-- 0 - 30 days -->
                    <th class="highlight text-center">Sell-Through Rate</th>
                    <th class="highlight text-center">Remaining</th>
                    <th class="highlight text-center">Value</th>
                    <th class="highlight text-center">Sold</th>
                    <!-- 31 - 60 days -->
                    <th class="text-center">Sell-Through Rate</th>
                    <th class="text-center">Remaining</th>
                    <th class="text-center">Value</th>
                    <th class="text-center">Sold</th>
                    <!-- 61 - 90 days -->
                    <th class="highlight text-center">Sell-Through Rate</th>
                    <th class="highlight text-center">Remaining</th>
                    <th class="highlight text-center">Value</th>
                    <th class="highlight text-center">Sold</th>
                    <!-- 91 - 120 days -->
                    <th class="text-center">Sell-Through Rate</th>
                    <th class="text-center">Remaining</th>
                    <th class="text-center">Value</th>
                    <th class="text-center">Sold</th>
                    <!-- 120 days -->
                    <th class="highlight text-center">Sell-Through Rate</th>
                    <th class="highlight text-center">Remaining</th>
                    <th class="highlight text-center">Value</th>
                    <th class="highlight text-center">Sold</th>
                  </tr>
                </thead>

                <tbody
                  v-for="(value, key) in packages.data"
                  class="table-rows with-border"
                >
                  <!-- Category row -->
                  <tr class="highlight">
                    <td class="text-left">
                      <strong>{{ key }}</strong>
                    </td>
                    <td class="text-left"><strong>Brand</strong></td>
                    <td class="text-left"><strong>Avail. Date</strong></td>
                    <!-- 0 - 30 days -->
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_30_days.sell_through || '-' }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_30_days.quantity }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ conditionalDollars(value.totals.to_30_days.value) }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_30_days.sold }}</strong>
                    </td>
                    <!-- 31 - 60 days -->
                    <td class="text-center">
                      <strong>{{ value.totals.to_60_days.sell_through || '-' }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ value.totals.to_60_days.quantity }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ conditionalDollars(value.totals.to_60_days.value) }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ value.totals.to_60_days.sold }}</strong>
                    </td>
                    <!-- 61 - 90 days -->
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_90_days.sell_through || '-' }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_90_days.quantity }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ conditionalDollars(value.totals.to_90_days.value) }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_90_days.sold }}</strong>
                    </td>
                    <!-- 91 - 120 days -->
                    <td class="text-center">
                      <strong>{{ value.totals.to_120_days.sell_through || '-' }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ value.totals.to_120_days.quantity }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ conditionalDollars(value.totals.to_120_days.value) }}</strong>
                    </td>
                    <td class="text-center">
                      <strong>{{ value.totals.to_120_days.sold }}</strong>
                    </td>
                    <!-- 120 days -->
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_120_plus_days.sell_through || '-' }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_120_plus_days.quantity }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ conditionalDollars(value.totals.to_120_plus_days.value) }}</strong>
                    </td>
                    <td class="highlight text-center">
                      <strong>{{ value.totals.to_120_plus_days.sold }}</strong>
                    </td>
                  </tr>
                  <!-- Entry row -->
                  <tr v-for="row in value.values">
                    <td>
                      {{ row.product_name }}
                      <br />
                      <span class="text-xs"> {{ row.batch_num }} | {{ row.lot_num }} </span>
                    </td>
                    <td>{{ row.product?.supplier?.name || 'NA' }}</td>
                    <td>{{ _date(row.first_available_at) }}</td>
                    <!-- 0 - 30 days -->
                    <td class="highlight text-center">{{ row.aging_inventory.to_30_days.sell_through ?? '-' }}</td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_30_days.quantity ?? '-' }}</td>
                    <td class="highlight text-center">
                      {{ conditionalDollars(row.aging_inventory.to_30_days.value) ?? '-' }}
                    </td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_30_days.sold }}</td>
                    <!-- 31 - 60 days -->
                    <td class="text-center">{{ row.aging_inventory.to_60_days.sell_through ?? '-' }}</td>
                    <td class="text-center">{{ row.aging_inventory.to_60_days.quantity ?? '-' }}</td>
                    <td class="text-center">{{ conditionalDollars(row.aging_inventory.to_60_days.value) ?? '-' }}</td>
                    <td class="text-center">{{ row.aging_inventory.to_60_days.sold ?? '-' }}</td>
                    <!-- 61 - 90 days -->
                    <td class="highlight text-center">{{ row.aging_inventory.to_90_days.sell_through ?? '-' }}</td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_90_days.quantity ?? '-' }}</td>
                    <td class="highlight text-center">
                      {{ conditionalDollars(row.aging_inventory.to_90_days.value) ?? '-' }}
                    </td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_90_days.sold ?? '-' }}</td>
                    <!-- 91 - 120 days -->
                    <td class="text-center">{{ row.aging_inventory.to_120_days.sell_through ?? '-' }}</td>
                    <td class="text-center">{{ row.aging_inventory.to_120_days.quantity ?? '-' }}</td>
                    <td class="text-center">{{ conditionalDollars(row.aging_inventory.to_120_days.value) ?? '-' }}</td>
                    <td class="text-center">{{ row.aging_inventory.to_120_days.sold ?? '-' }}</td>
                    <!-- 120 days -->
                    <td class="highlight text-center">
                      {{ row.aging_inventory.to_120_plus_days.sell_through ?? '-' }}
                    </td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_120_plus_days.quantity ?? '-' }}</td>
                    <td class="highlight text-center">
                      {{ conditionalDollars(row.aging_inventory.to_120_plus_days.value) ?? '-' }}
                    </td>
                    <td class="highlight text-center">{{ row.aging_inventory.to_120_plus_days.sold ?? '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- END TABLE -->
  </AdminLayout>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import formatters from '@/Mixins/formatters'
import isUrlAware from '@/Mixins/isUrlAware'
import _date from '@/Filters/Date'
import _dollars from '@/Filters/Dollars'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import accessesCompany from '@/Mixins/accessesCompany'

const trashedOptions = [
  { title: 'Active', current: true },
  { title: 'Archived', current: false }
]

export default {
  mixins: [formatters, isUrlAware, interactsWithCurrentLocation, accessesCompany],

  components: {
    AdminLayout,
    Head,
    Link
  },

  props: {
    /**
     * Query filter params.
     */
    params: Object,
    /**
     * Brands for filtering.
     */
    suppliers: Object,
    /**
     * Packages for rows
     */
    packages: Object
  },

  data() {
    return {
      form: this.$inertia.form({
        supplier_id: this.params.supplier_id || null,
        location_id: this.params.location_id
      })
    }
  },

  watch: {
    'form.location_id': function (newVal, oldVal) {
      this.update()
    },
    'form.supplier_id': function (newVal, oldVal) {
      this.update()
    }
  },

  computed: {},

  methods: {
    _date,
    _dollars,
    conditionalDollars(value) {
      if (!value) {
        return null
      }

      return _dollars(value)
    },
    update() {
      this.form.get(route('reports.aging-inventory.index', {}))
    },
    download() {
      window.open(route('reports.aging-inventory.index', { ...this.form, download: true }), '_blank')
    }
  }
}
</script>
